import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  ArrowLeft, Edit, ClipboardList, StickyNote, Camera, FileText,
  Plus, Clock, MapPin, User, Calendar, AlertCircle, Trash2, Download, Upload, Eye, EyeOff, Shield, RotateCcw,
  Users, Car, Timer, Lightbulb, MessageSquare, MessagesSquare, Network, UserPlus, X, FolderOpen, MoreHorizontal,
  Activity, Settings, ChevronDown, FileDown, Scale, Radar, FileSignature, Lock, Loader2, Mail, Phone,
  Archive, RefreshCw
} from 'lucide-react';
import SubjectsTabEnhanced from '../components/case/SubjectsTabEnhanced';
import WarrantOperationsTab from '../components/case/WarrantOperationsTab';
import VehiclesTab from '../components/case/VehiclesTab';
import TimelineTab from '../components/case/TimelineTab';
import PhotoGalleryTab from '../components/case/PhotoGalleryTab';
import LeadsTab from '../components/case/LeadsTab';
import InterviewsTab from '../components/case/InterviewsTab';
import LocationsTab from '../components/case/LocationsTab';
import CaseOverviewTab from '../components/case/CaseOverviewTab';
import TimeBillingTab from '../components/case/TimeBillingTab';
import FieldOpsTab from '../components/case/FieldOpsTab';
import OpsPlanTab from '../components/case/OpsPlanTab';
import SurveillanceReportsTab from '../components/case/SurveillanceReportsTab';
import CaseClosureWizard from '../components/CaseClosureWizard';
import CountyAssignmentPicker from '../components/CountyAssignmentPicker';
import { NxPanel, NxButton, NxChip } from '../components/nx';
import '../styles/case-detail-mdt.css';

// FBI-Style Dark Theme Status Colors
const STATUS_COLORS = {
  NEW: 'bg-[#0284C7]', PENDING_REVIEW: 'bg-[#D97706]', ASSIGNED: 'bg-[#3B82F6]',
  ACTIVE: 'bg-[#059669]', EVIDENCE_COLLECTION: 'bg-[#0891B2]', AWAITING_CLIENT_INFO: 'bg-[#EA580C]',
  UNDER_REVIEW: 'bg-[#4F46E5]', COMPLETED: 'bg-[#10B981]', CLOSED: 'bg-[#475569]'
};

const STATUSES = ['NEW','PENDING_REVIEW','ASSIGNED','ACTIVE','EVIDENCE_COLLECTION','AWAITING_CLIENT_INFO','UNDER_REVIEW','COMPLETED','CLOSED'];

const RISK_COLORS = { LOW: 'bg-[#059669]', MEDIUM: 'bg-[#D97706]', HIGH: 'bg-[#DC2626]', CRITICAL: 'bg-[#B91C1C]' };
const LEAD_STATUS_COLORS = { NEW: 'bg-[#0284C7]', INVESTIGATING: 'bg-[#D97706]', VERIFIED: 'bg-[#059669]', DEAD_END: 'bg-[#DC2626]', CLOSED: 'bg-[#475569]' };

const CaseDetail = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const token = localStorage.getItem('nexus_token');
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Master Directive #27 — case profile photo upload (hooks must live at
  // the top of the component, before any conditional early returns).
  const casePhotoInputRef = React.useRef(null);
  const [uploadingCasePhoto, setUploadingCasePhoto] = useState(false);
  const handleCasePhotoUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setUploadingCasePhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      setCaseData(prev => ({ ...prev, photo_url: data.photo_url }));
      toast({ title: 'Photo updated', description: 'Case profile photo saved' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setUploadingCasePhoto(false);
      if (casePhotoInputRef.current) casePhotoInputRef.current.value = '';
    }
  };
  const handleCasePhotoRemove = async () => {
    if (!window.confirm('Remove the case profile photo?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/photo`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Remove failed');
      setCaseData(prev => ({ ...prev, photo_url: null }));
      toast({ title: 'Removed', description: 'Case photo cleared' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };
  // Honour ?tab=<name> query string so routed sub-pages (e.g. LogFormPage
  // → /cases/:id?tab=logs) return the user to the tab they came from.
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'overview');

  // Keep local state and URL in sync when the user clicks a Tab trigger.
  const handleTabChange = (val) => {
    // Case Messages tab (Iter120L) opens the unified /messages page pinned
    // to this case rather than rendering a nested chat panel here.
    if (val === 'messages') {
      navigate(`/messages?case=${caseId}`);
      return;
    }
    setActiveTab(val);
    const next = new URLSearchParams(searchParams);
    if (val === 'overview') next.delete('tab');
    else next.set('tab', val);
    setSearchParams(next, { replace: true });
  };
  
  // Case Access Blocking State
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [blockingForm, setBlockingForm] = useState(null);

  // Intelligence module data
  const [subjects, setSubjects] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [leads, setLeads] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [locations, setLocations] = useState([]);

  // Dialogs
  const [showAddLog, setShowAddLog] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showUploadEvidence, setShowUploadEvidence] = useState(false);
  const [showStatusChange, setShowStatusChange] = useState(false);
  const [showAddLead, setShowAddLead] = useState(false);
  const [showAddInterview, setShowAddInterview] = useState(false);
  const [showAddTimeline, setShowAddTimeline] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showEvidenceCorrelation, setShowEvidenceCorrelation] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [showAssignUser, setShowAssignUser] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [assignForm, setAssignForm] = useState({ user_id: '', assignment_role: 'field_investigator', notes: '' });
  const [viewItem, setViewItem] = useState(null);
  const [viewType, setViewType] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showShareCase, setShowShareCase] = useState(false);
  const [showClosureWizard, setShowClosureWizard] = useState(false);
  // Iter126 — County Assignment (state/city/county for dashboard map pin)
  const [showCountyDialog, setShowCountyDialog] = useState(false);
  const [countyForm, setCountyForm] = useState(null);
  const [savingCounty, setSavingCounty] = useState(false);
  // Iter128b — Evidence trash / restore
  const [showEvidenceTrash, setShowEvidenceTrash] = useState(false);
  const [deletedEvidence, setDeletedEvidence] = useState([]);
  const [loadingTrash, setLoadingTrash] = useState(false);

  useEffect(() => {
    if (!showEvidenceTrash) return;
    setLoadingTrash(true);
    fetch(`${BACKEND_URL}/api/cases/${caseId}/evidence/trash`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setDeletedEvidence)
      .catch(() => setDeletedEvidence([]))
      .finally(() => setLoadingTrash(false));
  }, [showEvidenceTrash, caseId, token]);

  const restoreEvidence = async (evId) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/evidence/${evId}/restore`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        toast({ title: 'Evidence restored', description: 'File is back in the case.' });
        setDeletedEvidence(prev => prev.filter(e => e.id !== evId));
        fetchCase();
      } else {
        const err = await r.json().catch(() => ({}));
        toast({ title: 'Restore failed', description: err.detail || 'Server error', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };
  // Assign / Change Client dialog
  const [showAssignClient, setShowAssignClient] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [assigningClient, setAssigningClient] = useState(false);
  const [pendingClientId, setPendingClientId] = useState('');
  // Iter121D — CLIENT ACCESS: full list of clients attached to this case
  // via the new `client_case_assignments` collection (plus the legacy
  // primary client, if any). GET /api/cases/:id/clients.
  const [attachedClients, setAttachedClients] = useState([]);
  const [attachedClientsLoading, setAttachedClientsLoading] = useState(false);
  const [removingClientId, setRemovingClientId] = useState('');
  const [shareForm, setShareForm] = useState({
    target_company_id: '',
    target_user_ids: [],
    permission: 'full_access',
    notes: '',
    notify_admins: true,
    notify_users: true,
  });
  const [allCompanies, setAllCompanies] = useState([]);
  const [targetUsers, setTargetUsers] = useState([]);
  const [targetUsersLoading, setTargetUsersLoading] = useState(false);
  const [caseShares, setCaseShares] = useState([]);

  // Forms
  const [logForm, setLogForm] = useState({
    case_id: '', date: new Date().toISOString().split('T')[0], time_start: '09:00', time_end: '',
    location_address: '', city: '', state: '', narrative: '', activity_observed: '', additional_notes: ''
  });
  const [noteForm, setNoteForm] = useState({
    case_id: '', category: 'General', subject: '', content: '', visibility: 'INTERNAL_ONLY', follow_up_required: false
  });
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidenceDesc, setEvidenceDesc] = useState('');
  const [evidenceType, setEvidenceType] = useState('DOCUMENT');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newStatus, setNewStatus] = useState('');
  // Inline evidence preview (image/video/pdf) + download state
  const [previewEvidence, setPreviewEvidence] = useState(null); // evidence record
  const [previewBlobUrl, setPreviewBlobUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  // Intelligence module forms
  const [subjectForm, setSubjectForm] = useState({
    full_name: '', aliases: '', date_of_birth: '', gender: '', physical_description: '',
    height: '', weight: '', hair_color: '', eye_color: '', scars_tattoos_marks: '',
    employer: '', phone_numbers: '', email_addresses: '', criminal_history: '',
    surveillance_risk_level: 'MEDIUM', notes: ''
  });
  const [vehicleForm, setVehicleForm] = useState({
    license_plate: '', state: '', make: '', model: '', year: '', color: '',
    registered_owner: '', notes: ''
  });
  const [leadForm, setLeadForm] = useState({
    lead_source: '', description: '', priority: 'MEDIUM', notes: ''
  });
  const [interviewForm, setInterviewForm] = useState({
    interview_date: new Date().toISOString().split('T')[0], interview_location: '',
    interviewee_name: '', relationship_to_case: '', observations: '',
    body_language_notes: '', summary: ''
  });
  const [timelineForm, setTimelineForm] = useState({
    event_type: 'CUSTOM', event_date: new Date().toISOString().split('T')[0],
    event_time: '', location: '', description: ''
  });
  const [locationForm, setLocationForm] = useState({
    name: '', location_type: 'OTHER', address: '', city: '', state: '', zip_code: '',
    latitude: '', longitude: '', description: '', notes: ''
  });
  const [evidenceCorrelation, setEvidenceCorrelation] = useState({
    related_subjects: [], related_vehicles: [], related_locations: [], correlation_notes: ''
  });

  const isAdmin = user?.role === 'department_admin' || user?.role === 'main_hub';
  const canEdit = isAdmin || user?.role === 'field_investigator';
  const [exportingPDF, setExportingPDF] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);

  // Check if case access is blocked (pending acceptance form)
  const checkCaseAccess = useCallback(async () => {
    // Admins always have access
    if (isAdmin) {
      setAccessBlocked(false);
      return true;
    }
    // Master Directive #21 — CLIENT role is fully exempt from the acceptance
    // gate. Skip the round-trip entirely so any stale server response or
    // legacy pending form cannot re-trigger the block.
    if (user?.role === 'client') {
      setAccessBlocked(false);
      return true;
    }
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/case-acceptance/check-access/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (!data.access_allowed) {
          setAccessBlocked(true);
          setBlockingForm({ form_id: data.form_id, message: data.message });
          return false;
        }
      }
      setAccessBlocked(false);
      return true;
    } catch (e) {
      console.error('Access check error:', e);
      // If check fails, allow access (fail open for safety)
      return true;
    }
  }, [caseId, token, isAdmin, user?.role]);

  // Export case as PDF
  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `NexusTrac_Case_${caseData?.case_number || caseId}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast({ title: 'Success', description: 'Case PDF exported successfully' });
      } else {
        throw new Error('PDF export failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setExportingPDF(false);
    }
  };

  const fetchCase = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCaseData(data);
        setLogForm(prev => ({ ...prev, case_id: caseId }));
        setNoteForm(prev => ({ ...prev, case_id: caseId }));
      } else {
        toast({ title: 'Error', description: 'Case not found', variant: 'destructive' });
        navigate('/cases');
      }
    } catch (e) {
      console.error('Fetch case error:', e);
    } finally {
      setLoading(false);
    }
  }, [caseId, token, navigate]);

  const fetchIntelligence = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [subRes, vehRes, tlRes, leadRes, intRes, locRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/cases/${caseId}/subjects`, { headers }),
        fetch(`${BACKEND_URL}/api/cases/${caseId}/vehicles`, { headers }),
        fetch(`${BACKEND_URL}/api/cases/${caseId}/timeline`, { headers }),
        fetch(`${BACKEND_URL}/api/cases/${caseId}/leads`, { headers }),
        fetch(`${BACKEND_URL}/api/cases/${caseId}/interviews`, { headers }),
        fetch(`${BACKEND_URL}/api/cases/${caseId}/locations`, { headers })
      ]);
      if (subRes.ok) setSubjects(await subRes.json());
      if (vehRes.ok) setVehicles(await vehRes.json());
      if (tlRes.ok) setTimeline(await tlRes.json());
      if (leadRes.ok) setLeads(await leadRes.json());
      if (intRes.ok) setInterviews(await intRes.json());
      if (locRes.ok) setLocations(await locRes.json());
    } catch (e) {
      console.error('Fetch intelligence error:', e);
    }
  }, [caseId, token]);

  // Iter121D — load every client attached to this case (new relationship
  // + legacy primary client). Only admins and staff can view this.
  // Declared BEFORE the mount useEffect so React can reference it safely.
  const fetchAttachedClients = useCallback(async () => {
    if (user?.role === 'client') return;
    setAttachedClientsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAttachedClients(Array.isArray(data) ? data : []);
      } else if (res.status !== 403) {
        console.error('fetchAttachedClients HTTP', res.status);
      }
    } catch (e) {
      console.error('fetchAttachedClients error:', e);
    } finally {
      setAttachedClientsLoading(false);
    }
  }, [caseId, token, user?.role]);

  useEffect(() => {
    const initializeCase = async () => {
      const hasAccess = await checkCaseAccess();
      if (hasAccess) {
        fetchCase();
        fetchIntelligence();
        fetchAttachedClients();
      } else {
        // If access is blocked, stop loading
        setLoading(false);
      }
    };
    initializeCase();
  }, [fetchCase, fetchIntelligence, checkCaseAccess, fetchAttachedClients]);

  const handleAddLog = async () => {
    if (!logForm.narrative) {
      toast({ title: 'Error', description: 'Narrative is required', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...logForm, case_id: caseId })
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Case log added' });
        setShowAddLog(false);
        setLogForm({ case_id: caseId, date: new Date().toISOString().split('T')[0], time_start: '09:00', time_end: '', action: '', category: '', description: '', location_address: '', city: '', state: '', narrative: '', activity_observed: '', additional_notes: '' });
        fetchCase();
        fetchIntelligence();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleAddNote = async () => {
    if (!noteForm.content) {
      toast({ title: 'Error', description: 'Content is required', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...noteForm, case_id: caseId })
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Note added' });
        setShowAddNote(false);
        setNoteForm({ case_id: caseId, category: 'General', subject: '', content: '', visibility: 'INTERNAL_ONLY', follow_up_required: false });
        fetchCase();
        fetchIntelligence();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleUploadEvidence = async () => {
    if (!evidenceFile) {
      toast({ title: 'Error', description: 'Select a file', variant: 'destructive' });
      return;
    }
    setUploadProgress(0);
    setUploading(true);
    try {
      await new Promise((resolve, reject) => {
        const fd = new FormData();
        fd.append('file', evidenceFile);
        fd.append('description', evidenceDesc);
        fd.append('evidence_type', evidenceType);
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else {
            try { reject(new Error(JSON.parse(xhr.responseText).detail || `Upload failed (${xhr.status})`)); }
            catch { reject(new Error(`Upload failed (${xhr.status})`)); }
          }
        });
        xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
        xhr.open('POST', `${BACKEND_URL}/api/cases/${caseId}/evidence`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(fd);
      });
      toast({ title: 'Success', description: 'Evidence uploaded' });
      setShowUploadEvidence(false);
      setEvidenceFile(null);
      setEvidenceDesc('');
      fetchCase();
      fetchIntelligence();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast({ title: 'Success', description: `Status updated to ${newStatus.replace(/_/g, ' ')}` });
        setShowStatusChange(false);
        fetchCase();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Delete this log entry?')) return;
    await fetch(`${BACKEND_URL}/api/case-logs/${logId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchCase();
    fetchIntelligence();
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    await fetch(`${BACKEND_URL}/api/case-notes/${noteId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchCase();
    fetchIntelligence();
  };

  const handleDeleteEvidence = async (evidenceId) => {
    if (!window.confirm('Delete this evidence?')) return;
    await fetch(`${BACKEND_URL}/api/evidence/${evidenceId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchCase();
    fetchIntelligence();
  };

  // Intelligence module handlers
  // NOTE: Add Subject / Add Vehicle are now routed to dedicated pages:
  //   /cases/:caseId/subjects/new  ·  /cases/:caseId/vehicles/new
  // (See SubjectFormPage.jsx / VehicleFormPage.jsx). No popup handlers here.

  const handleDeleteSubject = async (subjectId) => {
    if (!window.confirm('Delete this subject?')) return;
    await fetch(`${BACKEND_URL}/api/subjects/${subjectId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchIntelligence();
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm('Delete this vehicle?')) return;
    await fetch(`${BACKEND_URL}/api/vehicles/${vehicleId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchIntelligence();
  };

  const handleAddLead = async () => {
    if (!leadForm.description) {
      toast({ title: 'Error', description: 'Description is required', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(leadForm)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Lead added' });
        setShowAddLead(false);
        setLeadForm({ lead_source: '', description: '', priority: 'MEDIUM', notes: '' });
        fetchIntelligence();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead?')) return;
    await fetch(`${BACKEND_URL}/api/leads/${leadId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchIntelligence();
  };

  const handleAddInterview = async () => {
    if (!interviewForm.interviewee_name) {
      toast({ title: 'Error', description: 'Interviewee name is required', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/interviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(interviewForm)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Interview recorded' });
        setShowAddInterview(false);
        setInterviewForm({ interview_date: new Date().toISOString().split('T')[0], interview_location: '', interviewee_name: '', relationship_to_case: '', observations: '', body_language_notes: '', summary: '' });
        fetchIntelligence();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDeleteInterview = async (interviewId) => {
    if (!window.confirm('Delete this interview?')) return;
    await fetch(`${BACKEND_URL}/api/interviews/${interviewId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchIntelligence();
  };

  const handleAddTimelineEvent = async () => {
    if (!timelineForm.description) {
      toast({ title: 'Error', description: 'Description is required', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/timeline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(timelineForm)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Timeline event added' });
        setShowAddTimeline(false);
        setTimelineForm({ event_type: 'CUSTOM', event_date: new Date().toISOString().split('T')[0], event_time: '', location: '', description: '' });
        fetchIntelligence();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDeleteTimelineEvent = async (eventId) => {
    if (!eventId.startsWith('TLE-')) return; // Only delete manual events
    if (!window.confirm('Delete this timeline event?')) return;
    await fetch(`${BACKEND_URL}/api/timeline/${eventId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchIntelligence();
  };

  // Location handlers
  const handleAddLocation = async () => {
    if (!locationForm.name) {
      toast({ title: 'Error', description: 'Location name is required', variant: 'destructive' });
      return;
    }
    try {
      const payload = {
        ...locationForm,
        latitude: locationForm.latitude ? parseFloat(locationForm.latitude) : null,
        longitude: locationForm.longitude ? parseFloat(locationForm.longitude) : null
      };
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/locations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Location added' });
        setShowAddLocation(false);
        setLocationForm({ name: '', location_type: 'OTHER', address: '', city: '', state: '', zip_code: '', latitude: '', longitude: '', description: '', notes: '' });
        fetchIntelligence();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Delete this location?')) return;
    await fetch(`${BACKEND_URL}/api/locations/${locationId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchIntelligence();
  };

  // Evidence correlation handlers
  const openEvidenceCorrelation = (evidence) => {
    setSelectedEvidence(evidence);
    setEvidenceCorrelation({
      related_subjects: evidence.related_subjects || [],
      related_vehicles: evidence.related_vehicles || [],
      related_locations: evidence.related_locations || [],
      correlation_notes: evidence.correlation_notes || ''
    });
    setShowEvidenceCorrelation(true);
  };

  const handleSaveEvidenceCorrelation = async () => {
    if (!selectedEvidence) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/evidence/${selectedEvidence.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(evidenceCorrelation)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Evidence correlations updated' });
        setShowEvidenceCorrelation(false);
        setSelectedEvidence(null);
        fetchCase();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const toggleCorrelation = (type, id) => {
    setEvidenceCorrelation(prev => {
      const arr = prev[type] || [];
      if (arr.includes(id)) {
        return { ...prev, [type]: arr.filter(x => x !== id) };
      } else {
        return { ...prev, [type]: [...arr, id] };
      }
    });
  };

  // Assign / Change Client — fetch company clients + PATCH the case
  const fetchClientsForAssign = async () => {
    setClientsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Backend returns an array; some tenants wrap it — support both shapes
        setAllClients(Array.isArray(data) ? data : (data.clients || []));
      } else {
        toast({ title: 'Error', description: 'Failed to load client list', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setClientsLoading(false);
    }
  };

  const openAssignClient = () => {
    setPendingClientId(caseData?.client_id || '');
    setClientSearch('');
    setShowAssignClient(true);
    if (allClients.length === 0) fetchClientsForAssign();
  };

  const handleAssignClient = async () => {
    if (!pendingClientId) {
      toast({ title: 'Error', description: 'Please pick a client', variant: 'destructive' });
      return;
    }
    setAssigningClient(true);
    try {
      // Iter121D — write via the authoritative `client_case_assignments`
      // relationship. The endpoint also back-fills the legacy pointer if
      // empty so the existing "Assigned Client" card stays populated.
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: pendingClientId }),
      });
      if (res.status === 409) {
        const err = await res.json().catch(() => ({}));
        toast({ title: 'Already assigned', description: err.detail || 'This client is already assigned to the case.' });
        return;
      }
      if (res.ok) {
        toast({ title: 'Client added', description: 'Client now has access to this case.' });
        setShowAssignClient(false);
        setPendingClientId('');
        await Promise.all([fetchCase(), fetchAttachedClients()]);
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: 'Error', description: err.detail || 'Failed to assign client', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setAssigningClient(false);
    }
  };

  // Iter121D — load every client attached to this case (new relationship
  // + legacy primary client). Only admins and staff can view this.
  const handleRemoveClientFromCase = async (clientRow) => {
    const label = clientRow.name
      || `${clientRow.first_name || ''} ${clientRow.last_name || ''}`.trim()
      || clientRow.business_name
      || clientRow.id;
    if (!window.confirm(`Remove ${label}'s access to this case?\n\nThe case, its evidence, notes and logs will NOT be deleted — only this client's access will be revoked.`)) return;
    setRemovingClientId(clientRow.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/clients/${clientRow.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast({ title: 'Client removed', description: `${label} no longer has access to this case.` });
        await Promise.all([fetchCase(), fetchAttachedClients()]);
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: 'Error', description: err.detail || 'Failed to remove client', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setRemovingClientId('');
    }
  };

  // Assignment management
  const fetchCompanyUsers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/company/investigators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setCompanyUsers(await res.json());
    } catch (e) {
      console.error('Fetch company users error:', e);
    }
  };

  const handleAssignUser = async () => {
    if (!assignForm.user_id) {
      toast({ title: 'Error', description: 'Select a user to assign', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(assignForm)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'User assigned to case' });
        setShowAssignUser(false);
        setAssignForm({ user_id: '', assignment_role: 'field_investigator', notes: '' });
        fetchCase();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Failed to assign user', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this assignment?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Assignment removed' });
        fetchCase();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // Cross-company sharing
  const fetchShares = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/shares`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setCaseShares(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/sharing/target-companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAllCompanies(await res.json());
      else toast({ title: 'Warning', description: 'Could not load target companies', variant: 'destructive' });
    } catch (e) { console.error(e); }
  };

  const fetchTargetCompanyUsers = async (companyId) => {
    if (!companyId) { setTargetUsers([]); return; }
    setTargetUsersLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/sharing/target-companies/${companyId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setTargetUsers(await res.json());
      else setTargetUsers([]);
    } catch (e) { console.error(e); setTargetUsers([]); }
    finally { setTargetUsersLoading(false); }
  };

  const handleShareCase = async () => {
    if (!shareForm.target_company_id) {
      toast({ title: 'Error', description: 'Select a target company', variant: 'destructive' });
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(shareForm)
      });
      if (res.ok) {
        const data = await res.json();
        toast({ title: 'Success', description: data.notified_users != null ? `Case shared. ${data.notified_users} user(s) notified.` : 'Case shared successfully' });
        setShowShareCase(false);
        setShareForm({ target_company_id: '', target_user_ids: [], permission: 'full_access', notes: '', notify_admins: true, notify_users: true });
        setTargetUsers([]);
        fetchShares();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Failed', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleRevokeShare = async (shareId) => {
    if (!window.confirm('Revoke this share?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/shares/${shareId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast({ title: 'Success', description: 'Share revoked' }); fetchShares(); }
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
  };

  // Iter121G — View/Edit routing. Every case entity now has a dedicated
  // read-only View page and an Edit page. The old inline dialog for
  // notes/logs/evidence is preserved as a fallback for types that still
  // ship without a routed pair.
  const openViewDialog = (item, type) => {
    const routedTypes = ['subject', 'vehicle', 'lead', 'interview', 'location', 'note'];
    if (routedTypes.includes(type)) {
      const collection = type === 'note' ? 'notes' : `${type}s`;
      navigate(`/cases/${caseId}/${collection}/${item.id}/view`);
      return;
    }
    setViewItem({ ...item });
    setViewType(type);
    setEditMode(false);
  };

  const openEditDialog = (item, type) => {
    const routedTypes = ['subject', 'vehicle', 'lead', 'interview', 'location', 'note'];
    if (routedTypes.includes(type)) {
      const collection = type === 'note' ? 'notes' : `${type}s`;
      navigate(`/cases/${caseId}/${collection}/${item.id}/edit`);
      return;
    }
    setViewItem({ ...item });
    setViewType(type);
    setEditMode(true);
  };

  // Delete handlers for case entities (used by tabs)
  const deleteEntity = async (type, id, confirmMsg) => {
    if (!window.confirm(confirmMsg || `Delete this ${type}?`)) return;
    const endpointMap = {
      subject: `/api/subjects/${id}`,
      vehicle: `/api/vehicles/${id}`,
      lead: `/api/leads/${id}`,
      interview: `/api/interviews/${id}`,
      location: `/api/locations/${id}`,
      timeline: `/api/timeline/${id}`,
    };
    try {
      const res = await fetch(`${BACKEND_URL}${endpointMap[type]}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Delete failed (${res.status})`);
      }
      toast({ title: 'Deleted', description: `${type} removed` });
      if (type === 'subject' || type === 'vehicle') fetchCase();
      else fetchIntelligence();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    }
  };

  // ========= Evidence file PREVIEW (inline image/pdf/video) =========
  const openEvidencePreview = async (ev) => {
    setPreviewEvidence(ev);
    setPreviewBlobUrl('');
    setPreviewError('');
    setPreviewLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/evidence/${ev.id}/view`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Preview failed (${res.status})`);
      }
      const blob = await res.blob();
      setPreviewBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      setPreviewError(e.message || 'Could not load preview');
    } finally {
      setPreviewLoading(false);
    }
  };
  const closeEvidencePreview = () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl('');
    setPreviewEvidence(null);
    setPreviewError('');
    setPreviewLoading(false);
  };

  // ========= Evidence file DOWNLOAD (auth-aware, with error toast) =========
  const handleEvidenceDownload = async (ev) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/evidence/${ev.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = ev.file_name || `evidence-${ev.id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast({ title: 'Download started', description: ev.file_name || 'Evidence file' });
    } catch (e) {
      toast({ title: 'Download failed', description: e.message, variant: 'destructive' });
    }
  };

  const handleUpdateItem = async () => {
    const endpointMap = {
      subject: `/api/subjects/${viewItem.id}`,
      vehicle: `/api/vehicles/${viewItem.id}`,
      lead: `/api/leads/${viewItem.id}`,
      interview: `/api/interviews/${viewItem.id}`,
      location: `/api/locations/${viewItem.id}`,
      note: `/api/case-notes/${viewItem.id}`,
      log: `/api/case-logs/${viewItem.id}`,
      evidence: `/api/evidence/${viewItem.id}`,
    };
    try {
      const res = await fetch(`${BACKEND_URL}${endpointMap[viewType]}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(viewItem)
      });
      if (res.ok) {
        toast({ title: 'Success', description: `${viewType} updated` });
        setViewItem(null);
        setEditMode(false);
        fetchCase(); // Refresh case data to get updated notes/logs/evidence
        fetchIntelligence();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Update failed', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const ViewField = useCallback(({ label, value }) => (
    <div className="space-y-1">
      <p className="text-xs text-[#94A3B8] uppercase tracking-wider">{label}</p>
      <p className="text-sm text-[#F8FAFC]">{value || <span className="text-[#475569] italic">Not set</span>}</p>
    </div>
  ), []);

  const EditField = useCallback(({ label, field, type = 'text', rows }) => (
    <div className="space-y-1">
      <Label className="text-xs text-[#94A3B8]">{label}</Label>
      {rows ? (
        <Textarea value={viewItem?.[field] || ''} onChange={e => setViewItem({ ...viewItem, [field]: e.target.value })}
          className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC] text-sm" rows={rows} />
      ) : (
        <Input type={type} value={viewItem?.[field] || ''} onChange={e => setViewItem({ ...viewItem, [field]: e.target.value })}
          className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC] text-sm" />
      )}
    </div>
  ), [viewItem]);

  const renderViewEditFields = () => {
    if (!viewItem) return null;
    const F = editMode ? EditField : ViewField;
    const fProps = editMode ? (l, f, t, r) => ({ label: l, field: f, type: t, rows: r }) : (l, f) => ({ label: l, value: viewItem[f] });

    switch (viewType) {
      case 'subject': return (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><F {...fProps('Full Name', 'full_name')} /></div>
          <div className="col-span-2"><F {...fProps('Subject Type', 'role_in_case')} /></div>
          <F {...fProps('Aliases', 'aliases')} />
          <F {...fProps('Date of Birth', 'date_of_birth', 'date')} />
          <F {...fProps('Gender', 'gender')} />
          <F {...fProps('Height', 'height')} />
          <F {...fProps('Weight', 'weight')} />
          <F {...fProps('Hair Color', 'hair_color')} />
          <F {...fProps('Eye Color', 'eye_color')} />
          <F {...fProps('Employer', 'employer')} />
          <div className="col-span-2"><F {...fProps('Physical Description', 'physical_description', 'text', 2)} /></div>
          <div className="col-span-2"><F {...fProps('Scars/Tattoos/Marks', 'scars_tattoos_marks', 'text', 2)} /></div>
          <F {...fProps('Phone Numbers', 'phone_numbers')} />
          <F {...fProps('Email Addresses', 'email_addresses')} />
          <div className="col-span-2"><F {...fProps('Criminal History', 'criminal_history', 'text', 2)} /></div>
          <div className="col-span-2"><F {...fProps('Notes', 'notes', 'text', 2)} /></div>
        </div>
      );
      case 'vehicle': return (
        <div className="grid grid-cols-2 gap-3">
          <F {...fProps('License Plate', 'license_plate')} />
          <F {...fProps('State', 'state')} />
          <F {...fProps('Make', 'make')} />
          <F {...fProps('Model', 'model')} />
          <F {...fProps('Year', 'year')} />
          <F {...fProps('Color', 'color')} />
          <div className="col-span-2"><F {...fProps('Registered Owner', 'registered_owner')} /></div>
          <div className="col-span-2"><F {...fProps('Notes', 'notes', 'text', 2)} /></div>
        </div>
      );
      case 'lead': return (
        <div className="grid grid-cols-2 gap-3">
          <F {...fProps('Lead Source', 'lead_source')} />
          <F {...fProps('Priority', 'priority')} />
          <div className="col-span-2"><F {...fProps('Description', 'description', 'text', 3)} /></div>
          <div className="col-span-2"><F {...fProps('Notes', 'notes', 'text', 2)} /></div>
        </div>
      );
      case 'interview': return (
        <div className="grid grid-cols-2 gap-3">
          <F {...fProps('Interviewee', 'interviewee_name')} />
          <F {...fProps('Date', 'interview_date', 'date')} />
          <F {...fProps('Location', 'interview_location')} />
          <F {...fProps('Relationship to Case', 'relationship_to_case')} />
          <div className="col-span-2"><F {...fProps('Summary', 'summary', 'text', 3)} /></div>
          <div className="col-span-2"><F {...fProps('Observations', 'observations', 'text', 2)} /></div>
          <div className="col-span-2"><F {...fProps('Body Language Notes', 'body_language_notes', 'text', 2)} /></div>
        </div>
      );
      case 'location': return (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><F {...fProps('Name', 'name')} /></div>
          <F {...fProps('Type', 'location_type')} />
          <div className="col-span-2"><F {...fProps('Address', 'address')} /></div>
          <F {...fProps('City', 'city')} />
          <F {...fProps('State', 'state')} />
          <F {...fProps('Zip Code', 'zip_code')} />
          <F {...fProps('Latitude', 'latitude')} />
          <F {...fProps('Longitude', 'longitude')} />
          <div className="col-span-2"><F {...fProps('Description', 'description', 'text', 2)} /></div>
          <div className="col-span-2"><F {...fProps('Notes', 'notes', 'text', 2)} /></div>
        </div>
      );
      case 'note': return (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><F {...fProps('Subject', 'subject')} /></div>
          <F {...fProps('Category', 'category')} />
          <F {...fProps('Visibility', 'visibility')} />
          <div className="col-span-2"><F {...fProps('Content', 'content', 'text', 6)} /></div>
          <F {...fProps('Follow-up Required', 'follow_up_required')} />
          <F {...fProps('Follow-up Date', 'follow_up_date', 'date')} />
          <div className="col-span-2 pt-3 border-t border-[#334155]">
            <p className="text-xs text-[#94A3B8]">Author: {viewItem?.author_name}</p>
            <p className="text-xs text-[#94A3B8]">Created: {viewItem?.created_at ? new Date(viewItem.created_at).toLocaleString() : 'N/A'}</p>
          </div>
        </div>
      );
      case 'log': return (
        <div className="grid grid-cols-2 gap-3">
          <F {...fProps('Date', 'date', 'date')} />
          <F {...fProps('Time Start', 'time_start')} />
          <F {...fProps('Time End', 'time_end')} />
          <F {...fProps('Action', 'action')} />
          <F {...fProps('Category', 'category')} />
          <div className="col-span-2"><F {...fProps('Description', 'description', 'text', 3)} /></div>
          <div className="col-span-2"><F {...fProps('Location', 'location_address')} /></div>
          <F {...fProps('City', 'city')} />
          <F {...fProps('State', 'state')} />
          <div className="col-span-2"><F {...fProps('Narrative', 'narrative', 'text', 4)} /></div>
          <div className="col-span-2"><F {...fProps('Activity Observed', 'activity_observed', 'text', 2)} /></div>
          <div className="col-span-2"><F {...fProps('Additional Notes', 'additional_notes', 'text', 2)} /></div>
          <div className="col-span-2 pt-3 border-t border-[#334155]">
            <p className="text-xs text-[#94A3B8]">Logged by: {viewItem?.investigator_name || 'N/A'}</p>
            <p className="text-xs text-[#94A3B8]">Created: {viewItem?.created_at ? new Date(viewItem.created_at).toLocaleString() : 'N/A'}</p>
          </div>
        </div>
      );
      case 'evidence': return (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><F {...fProps('File Name', 'file_name')} /></div>
          <F {...fProps('Evidence Type', 'evidence_type')} />
          <F {...fProps('File Size', 'file_size')} />
          <div className="col-span-2"><F {...fProps('Description', 'description', 'text', 3)} /></div>
          <F {...fProps('Chain of Custody', 'chain_of_custody')} />
          <div className="col-span-2 pt-3 border-t border-[#334155]">
            <p className="text-xs text-[#94A3B8]">Uploaded by: {viewItem?.uploaded_by_name}</p>
            <p className="text-xs text-[#94A3B8]">Uploaded: {viewItem?.uploaded_at ? new Date(viewItem.uploaded_at).toLocaleString() : 'N/A'}</p>
          </div>
        </div>
      );
      default: return null;
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-500">Loading case details...</div>;
  }

  // Case Access Blocked - Show blocking modal
  if (accessBlocked && blockingForm) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="case-access-blocked">
        <Card className="bg-slate-900 border-amber-500/50 max-w-lg w-full mx-4">
          <CardHeader className="text-center border-b border-amber-500/30 pb-6">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Lock className="w-10 h-10 text-amber-500" />
              </div>
            </div>
            <CardTitle className="text-xl text-white">Case Access Restricted</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 text-center space-y-4">
            <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4">
              <FileSignature className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-slate-300">{blockingForm.message}</p>
            </div>
            <p className="text-slate-400 text-sm">
              Before you can access this case, you must review and sign the Case Assignment Acceptance Agreement.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                onClick={() => navigate('/cases')}
                variant="outline"
                className="flex-1 border-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Cases
              </Button>
              <Button
                onClick={() => navigate(`/case-acceptance/${blockingForm.form_id}`)}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                <FileSignature className="w-4 h-4 mr-2" /> Sign Acceptance Form
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Iter132 — Case Lockout: show a crisp "blank + sign" screen for
  // assigned non-admin investigators until the Case Acceptance form is
  // signed.  Admins bypass this entirely (per user directive).  The
  // card still redirects to the Case Acceptance module when the user
  // taps "Sign Acceptance Form".
  if (accessBlocked && blockingForm) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="case-access-blocked">
        <Card className="w-full max-w-lg bg-slate-900/60 border border-amber-500/40">
          <CardContent className="pt-6 text-center space-y-5">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
              <Lock className="w-7 h-7 text-amber-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white mb-1">Case Locked</h2>
              <p className="text-slate-300 text-sm">
                {blockingForm.message || 'Sign the Case Assignment Acceptance Agreement to unlock this case.'}
              </p>
              <p className="text-slate-500 text-xs mt-2">
                Every case tab stays hidden until you typed-sign the acceptance form.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button onClick={() => navigate('/cases')} variant="outline"
                className="flex-1 border-slate-600 text-slate-200"
                data-testid="case-locked-back">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Cases
              </Button>
              <Button onClick={() => navigate(`/case-acceptance/${blockingForm.form_id}`)}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="case-locked-sign">
                <FileSignature className="w-4 h-4 mr-2" /> Sign Acceptance Form
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!caseData) {
    return <div className="text-center py-12 text-slate-500">Case not found</div>;
  }

  const visibilityIcon = (v) => {
    if (v === 'ADMIN_ONLY') return <Shield className="w-3 h-3 text-red-400" />;
    if (v === 'CLIENT_VIEWABLE') return <Eye className="w-3 h-3 text-green-400" />;
    return <EyeOff className="w-3 h-3 text-slate-500" />;
  };


  return (
    <div className="space-y-6 nx-case-detail" data-testid="case-detail-page">
      {/* MDT Case Header — incident-style, amber left-rail */}
      <div className="nx-cd-header" data-testid="case-mdt-header">
        <Link to="/cases" className="nx-cd-back" data-testid="case-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Cases
        </Link>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          {/* Left: incident identity */}
          <div className="min-w-0 flex-1">
            {/* MASTER DIRECTIVE #27 — Case profile photo hero */}
            <div className="flex items-start gap-4 mb-2">
              <div className="relative flex-shrink-0" data-testid="case-photo-hero">
                {caseData.photo_url ? (
                  <img
                    src={caseData.photo_url}
                    alt="Case profile"
                    className="w-24 h-24 rounded-lg object-cover border-2 border-slate-700 shadow-md"
                    data-testid="case-photo-img"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-600 bg-slate-800/40 flex items-center justify-center text-slate-500">
                    <Camera className="w-8 h-8" />
                  </div>
                )}
                {canEdit && (
                  <div className="absolute -bottom-1 -right-1 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => casePhotoInputRef.current?.click()}
                      disabled={uploadingCasePhoto}
                      className="bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-full w-7 h-7 flex items-center justify-center shadow-md border border-slate-900"
                      title={caseData.photo_url ? 'Replace case photo' : 'Add case photo'}
                      data-testid="case-photo-upload-btn"
                    >
                      {uploadingCasePhoto ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    </button>
                    {caseData.photo_url && (
                      <button
                        type="button"
                        onClick={handleCasePhotoRemove}
                        className="bg-red-500 hover:bg-red-400 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-md border border-slate-900"
                        title="Remove case photo"
                        data-testid="case-photo-remove-btn"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <input
                      ref={casePhotoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleCasePhotoUpload}
                      data-testid="case-photo-file-input"
                    />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="nx-cd-eyebrow" data-testid="case-number">
                    {caseData.case_number}
                  </span>
                  <NxChip
                    tone={
                      caseData.status === 'ACTIVE' || caseData.status === 'COMPLETED' ? 'lime'
                      : caseData.status === 'CLOSED' ? 'blue'
                      : caseData.status?.includes('REVIEW') || caseData.status === 'AWAITING_CLIENT_INFO' ? 'amber'
                      : 'cyan'
                    }
                    data-testid="case-status-badge"
                  >
                    {caseData.status?.replace(/_/g, ' ')}
                  </NxChip>
                  {caseData.priority === 'URGENT' && <NxChip tone="solid-red">URGENT</NxChip>}
                  {caseData.priority === 'HIGH' && <NxChip tone="solid-amber">HIGH</NxChip>}
                </div>

                <h1 className="nx-cd-title">{caseData.title}</h1>

                <p className="nx-cd-meta">
                  <span>{caseData.case_type?.replace(/_/g, ' ')}</span>
                  {caseData.jurisdiction && <><span className="sep">·</span><span>{caseData.jurisdiction}</span></>}
                </p>
              </div>
            </div>
          </div>

          {/* Right: command actions — desktop */}
          <div className="hidden md:flex gap-2 flex-wrap items-start" data-testid="case-action-bar">
            {['BAIL_BOND_APPREHENSION', 'BAIL_BOND', 'WARRANT', 'FUGITIVE_RECOVERY', 'SKIP_TRACE', 'MISSING_PERSON'].includes(caseData.case_type) && (
              <NxButton
                variant="primary"
                className="nx-btn--sm"
                onClick={() => navigate(`/skip-trace?caseId=${caseId}`)}
                data-testid="open-skip-trace-btn"
              >
                <Radar className="w-4 h-4" /> Open Skip Trace
              </NxButton>
            )}
            <NxButton variant="secondary" className="nx-btn--sm" onClick={() => navigate(`/cases/${caseId}/notes/new`)} data-testid="add-note-quick-btn">
              <StickyNote className="w-4 h-4" /> Add Note
            </NxButton>
            <NxButton variant="secondary" className="nx-btn--sm" onClick={() => navigate(`/cases/${caseId}/evidence/new`)} data-testid="add-evidence-quick-btn">
              <Camera className="w-4 h-4" /> Add Evidence
            </NxButton>
            <NxButton variant="secondary" className="nx-btn--sm" onClick={handleExportPDF} disabled={exportingPDF} data-testid="export-pdf-btn">
              <FileDown className={`w-4 h-4 ${exportingPDF ? 'animate-pulse' : ''}`} />
              {exportingPDF ? 'Exporting…' : 'Export PDF'}
            </NxButton>
            {canEdit && (
              <NxButton variant="secondary" className="nx-btn--sm" onClick={() => navigate(`/cases/${caseId}/status`)} data-testid="change-status-btn">
                <Edit className="w-4 h-4" /> Change Status
              </NxButton>
            )}
            {(user?.role === 'main_hub' || user?.role === 'department_admin') && (
              <NxButton
                variant="secondary"
                className="nx-btn--sm"
                onClick={() => navigate(`/cases/${caseId}/share`)}
                data-testid="share-case-header-btn"
              >
                <FolderOpen className="w-4 h-4" /> Share Case
              </NxButton>
            )}
            {(user?.role === 'main_hub' || user?.role === 'department_admin') && caseData.status !== 'CLOSED' && !caseData.is_closed && (
              <NxButton
                variant="secondary"
                className="nx-btn--sm"
                onClick={() => setShowClosureWizard(true)}
                data-testid="close-case-btn"
                style={{ borderColor: '#F59E0B', color: '#FCD34D' }}
              >
                <Archive className="w-4 h-4" /> Close Case
              </NxButton>
            )}
            {(caseData.status === 'CLOSED' || caseData.is_closed) && (user?.role === 'main_hub' || user?.role === 'department_admin') && (
              <NxButton
                variant="secondary"
                className="nx-btn--sm"
                onClick={async () => {
                  const reason = window.prompt('Reopen reason:');
                  if (!reason) return;
                  const r = await fetch(`${BACKEND_URL}/api/cases/${caseId}/reopen`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('nexus_token')}` },
                    body: JSON.stringify({ reason }),
                  });
                  if (r.ok) { toast({ title: 'Case reopened' }); window.location.reload(); }
                }}
                data-testid="reopen-case-btn"
              >
                <RefreshCw className="w-4 h-4" /> Reopen Case
              </NxButton>
            )}
          </div>

          {/* Mobile quick actions */}
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="w-full border-[#334155] text-[#94A3B8] bg-[#131A2A]">
                  <Plus className="w-4 h-4 mr-2" /> Quick Actions
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-auto rounded-t-lg bg-[#131A2A] border-t border-[#334155]">
                <SheetHeader>
                  <SheetTitle className="text-[#F8FAFC]">Quick Actions</SheetTitle>
                </SheetHeader>
                <div className="grid grid-cols-2 gap-3 py-4">
                  {['BAIL_BOND_APPREHENSION', 'BAIL_BOND', 'WARRANT', 'FUGITIVE_RECOVERY', 'SKIP_TRACE', 'MISSING_PERSON'].includes(caseData.case_type) && (
                    <Button
                      className="h-14 flex-col gap-1 bg-[#3B82F6] hover:bg-[#60A5FA] text-white col-span-2"
                      onClick={() => navigate(`/skip-trace?caseId=${caseId}`)}
                      data-testid="open-skip-trace-btn-mobile"
                    >
                      <Radar className="w-5 h-5" />
                      <span className="text-xs font-semibold">Open Skip Trace Workspace</span>
                    </Button>
                  )}
                  <Button variant="outline" className="h-14 flex-col gap-1 border-[#334155] bg-[#0B0F19] text-[#94A3B8]" onClick={() => navigate(`/cases/${caseId}/notes/new`)}>
                    <StickyNote className="w-5 h-5" />
                    <span className="text-xs">Add Note</span>
                  </Button>
                  <Button variant="outline" className="h-14 flex-col gap-1 border-[#334155] bg-[#0B0F19] text-[#94A3B8]" onClick={() => navigate(`/cases/${caseId}/evidence/new`)}>
                    <Camera className="w-5 h-5" />
                    <span className="text-xs">Add Evidence</span>
                  </Button>
                  <Button variant="outline" className="h-14 flex-col gap-1 border-[#334155] bg-[#0B0F19] text-[#94A3B8]" onClick={() => navigate(`/cases/${caseId}/leads/new`)}>
                    <Lightbulb className="w-5 h-5" />
                    <span className="text-xs">Add Lead</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-14 flex-col gap-1 border-[#1D4ED8] bg-[#0B0F19] text-[#3B82F6]"
                    onClick={handleExportPDF}
                    disabled={exportingPDF}
                  >
                    <FileDown className={`w-5 h-5 ${exportingPDF ? 'animate-pulse' : ''}`} />
                    <span className="text-xs">{exportingPDF ? 'Exporting...' : 'Export PDF'}</span>
                  </Button>
                  {canEdit && (
                    <Button
                      variant="outline"
                      className="h-14 flex-col gap-1 border-[#334155] bg-[#0B0F19] text-[#94A3B8] col-span-2"
                      onClick={() => navigate(`/cases/${caseId}/status`)}
                    >
                      <Edit className="w-5 h-5" />
                      <span className="text-xs">Change Status</span>
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Iter121D — CLIENT ACCESS panel. Shows every client attached to
          the case through the authoritative `client_case_assignments`
          collection (plus the legacy primary client for backward compat).
          Admins can add + remove clients from either side of the
          bidirectional relationship. */}
      {(user?.role === 'main_hub' || user?.role === 'department_admin' || user?.role === 'field_investigator') && (
        <div
          className="rounded-lg border border-[#334155] bg-[#131A2A] p-4"
          data-testid="case-client-access-panel"
        >
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-[#94A3B8]">Client Access</p>
              <p className="text-xs text-[#64748B] mt-0.5">
                Clients granted portal access to this case.
              </p>
            </div>
            {isAdmin && (
              <NxButton
                variant="secondary"
                className="nx-btn--sm shrink-0"
                onClick={() => {
                  setCountyForm({
                    state: caseData.state || '',
                    state_name: caseData.state_name || '',
                    city: caseData.city || '',
                    county: caseData.county || '',
                    latitude: caseData.latitude || null,
                    longitude: caseData.longitude || null,
                  });
                  setShowCountyDialog(true);
                }}
                data-testid="assign-county-btn"
                style={{ borderColor: '#0EA5E9', color: '#7DD3FC' }}
              >
                <MapPin className="w-4 h-4" /> {caseData.county ? `${caseData.city || ''}${caseData.city ? ', ' : ''}${caseData.county}` : 'Assign County'}
              </NxButton>
            )}
            {isAdmin && (
              <NxButton
                variant="secondary"
                className="nx-btn--sm shrink-0"
                onClick={openAssignClient}
                data-testid="assign-client-btn"
              >
                <UserPlus className="w-4 h-4" /> Add Client
              </NxButton>
            )}
          </div>

          {attachedClientsLoading ? (
            <div className="flex items-center gap-2 text-sm text-[#94A3B8]" data-testid="client-access-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading clients…
            </div>
          ) : attachedClients.length === 0 ? (
            <div className="text-sm text-amber-400" data-testid="client-access-empty">
              No clients have access to this case yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="client-access-list">
              {attachedClients.map((c) => {
                const display = c.name
                  || `${c.first_name || ''} ${c.last_name || ''}`.trim()
                  || c.business_name
                  || 'Unnamed';
                const email = c.primary_email || c.email;
                const phone = c.primary_phone || c.phone;
                return (
                  <div
                    key={c.id}
                    className="bg-[#0B0F19] border border-[#1E293B] rounded-md p-3 flex flex-col gap-2"
                    data-testid={`client-access-card-${c.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[#F8FAFC] truncate" data-testid={`client-access-name-${c.id}`}>
                          {display}
                        </p>
                        {c.business_name && c.business_name !== display && (
                          <p className="text-xs text-[#94A3B8] truncate">{c.business_name}</p>
                        )}
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-[#94A3B8] mt-0.5">
                          {email && (
                            <a href={`mailto:${email}`} className="text-[#3B82F6] hover:underline break-all" data-testid={`client-access-email-${c.id}`}>
                              <Mail className="inline w-3 h-3 mr-0.5" /> {email}
                            </a>
                          )}
                          {phone && (
                            <a href={`tel:${phone}`} className="text-[#3B82F6] hover:underline">
                              <Phone className="inline w-3 h-3 mr-0.5" /> {phone}
                            </a>
                          )}
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className={`text-[10px] px-2 py-0 h-5 flex-shrink-0 ${c.portal_enabled ? 'border-emerald-500/50 text-emerald-400 bg-emerald-500/10' : 'border-slate-500/50 text-slate-400 bg-slate-500/10'}`}
                      >
                        {c.portal_enabled ? 'Portal On' : 'Portal Off'}
                      </Badge>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#334155] !text-[#94A3B8] hover:bg-[#182136] flex-1"
                          onClick={() => navigate(`/clients/${c.id}/view`)}
                          data-testid={`client-access-open-${c.id}`}
                        >
                          <User className="w-3.5 h-3.5 mr-1" /> Profile
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-700/60 !text-red-300 hover:bg-red-900/30"
                          onClick={() => handleRemoveClientFromCase(c)}
                          disabled={removingClientId === c.id}
                          data-testid={`client-access-remove-${c.id}`}
                        >
                          {removingClientId === c.id
                            ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> …</>
                            : <><Trash2 className="w-3.5 h-3.5 mr-1" /> Remove</>}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* MDT Stat Tiles — incident readouts */}
      <div className="nx-cd-stats" data-testid="case-stats">
        <div className="nx-cd-stat">
          <p className="nx-cd-stat-label">Case Type</p>
          <p className="nx-cd-stat-value">{caseData.case_type?.replace(/_/g, ' ') || '—'}</p>
        </div>
        <div className="nx-cd-stat nx-cd-stat--blue">
          <p className="nx-cd-stat-label">Subjects</p>
          <p className="nx-cd-stat-value" data-testid="stat-subjects">{subjects.length}</p>
        </div>
        <div className="nx-cd-stat nx-cd-stat--blue">
          <p className="nx-cd-stat-label">Vehicles</p>
          <p className="nx-cd-stat-value" data-testid="stat-vehicles">{vehicles.length}</p>
        </div>
        <div className="nx-cd-stat nx-cd-stat--lime">
          <p className="nx-cd-stat-label">Leads</p>
          <p className="nx-cd-stat-value" data-testid="stat-leads">{leads.length}</p>
        </div>
        <div className="nx-cd-stat nx-cd-stat--red">
          <p className="nx-cd-stat-label">Evidence</p>
          <p className="nx-cd-stat-value" data-testid="stat-evidence">{caseData.evidence?.length || 0}</p>
        </div>
      </div>

      {/* Navigation Tabs - MDT auto-themed via case-detail-mdt.css */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        {/* Desktop Navigation */}
        <div className="hidden md:block">
          <TabsList className="bg-[#131A2A] border border-[#334155] flex-wrap h-auto gap-1 p-1.5 shadow-sm">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8] px-3 py-2 text-sm">
              Overview
            </TabsTrigger>
            <TabsTrigger value="subjects" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8] px-3 py-2 text-sm">
              <Users className="w-4 h-4 mr-1" />Subjects <Badge variant="secondary" className="ml-1 text-xs bg-[#182136] text-[#94A3B8]">{subjects.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="vehicles" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8] px-3 py-2 text-sm">
              <Car className="w-4 h-4 mr-1" />Vehicles <Badge variant="secondary" className="ml-1 text-xs bg-[#182136] text-[#94A3B8]">{vehicles.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="timeline" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8] px-3 py-2 text-sm">
              <Timer className="w-4 h-4 mr-1" />Timeline <Badge variant="secondary" className="ml-1 text-xs bg-[#182136] text-[#94A3B8]">{timeline.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="leads" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8] px-3 py-2 text-sm">
              <Lightbulb className="w-4 h-4 mr-1" />Leads <Badge variant="secondary" className="ml-1 text-xs bg-[#182136] text-[#94A3B8]">{leads.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="interviews" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8] px-3 py-2 text-sm">
              <MessageSquare className="w-4 h-4 mr-1" />Interviews <Badge variant="secondary" className="ml-1 text-xs bg-[#182136] text-[#94A3B8]">{interviews.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="locations" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8] px-3 py-2 text-sm">
              <MapPin className="w-4 h-4 mr-1" />Locations <Badge variant="secondary" className="ml-1 text-xs bg-[#182136] text-[#94A3B8]">{locations.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white text-[#94A3B8] px-3 py-2 text-sm">
              Logs <Badge variant="secondary" className="ml-1 text-xs bg-[#182136] text-[#94A3B8]">{caseData.logs?.length || 0}</Badge>
            </TabsTrigger>
            <TabsTrigger value="notes" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md px-3 py-2 text-sm">
              Notes <Badge variant="secondary" className="ml-1 text-xs">{caseData.notes?.length || 0}</Badge>
            </TabsTrigger>
            <TabsTrigger value="evidence" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md px-3 py-2 text-sm">
              Evidence <Badge variant="secondary" className="ml-1 text-xs">{caseData.evidence?.length || 0}</Badge>
            </TabsTrigger>
            <TabsTrigger value="photos" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md px-3 py-2 text-sm" data-testid="case-photos-tab-trigger">
              <Camera className="w-4 h-4 mr-1" />Photos
            </TabsTrigger>
            {['BAIL_BOND_APPREHENSION', 'BAIL_BOND', 'WARRANT', 'FUGITIVE_RECOVERY', 'SKIP_TRACE'].includes(caseData.case_type) && (
              <TabsTrigger value="warrant_ops" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md px-3 py-2 text-sm" data-testid="warrant-ops-desktop-tab">
                <Scale className="w-4 h-4 mr-1" />ATA Warrants
              </TabsTrigger>
            )}
            <TabsTrigger value="reports" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md px-3 py-2 text-sm">
              Reports <Badge variant="secondary" className="ml-1 text-xs">{caseData.reports?.length || 0}</Badge>
            </TabsTrigger>
            {/* Iter122 — Field Ops tab (Time now lives under top-level Field Operations menu; Surveillance is generated from within Field Ops after data is logged) */}
            <TabsTrigger value="field_ops" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white rounded-md px-3 py-2 text-sm" data-testid="case-field-ops-tab-trigger">
              <MapPin className="w-4 h-4 mr-1" /> Field Ops
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="ops_plan" className="data-[state=active]:bg-blue-700 data-[state=active]:text-white rounded-md px-3 py-2 text-sm" data-testid="case-ops-plan-tab-trigger">
                <Eye className="w-4 h-4 mr-1" /> Ops Plan
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="assignments" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md px-3 py-2 text-sm">
                <UserPlus className="w-4 h-4 mr-1" />Assignments <Badge variant="secondary" className="ml-1 text-xs">{caseData.assignments?.length || 0}</Badge>
              </TabsTrigger>
            )}
            <TabsTrigger value="messages" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md px-3 py-2 text-sm" data-testid="case-messages-tab">
              <MessagesSquare className="w-4 h-4 mr-1" />Messages
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Mobile Navigation - Grouped */}
        <div className="md:hidden">
          <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-hide">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'overview' ? 'bg-[#0F172A] text-white' : 'bg-white border border-slate-200 text-slate-600'
              }`}
            >
              Overview
            </button>
            
            {/* People Group */}
            <Sheet>
              <SheetTrigger asChild>
                <button className={`flex-shrink-0 flex items-center gap-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  ['subjects', 'interviews'].includes(activeTab) 
                    ? 'bg-[#0F172A] text-white' 
                    : 'bg-white border border-slate-200 text-slate-600'
                }`}>
                  <Users className="w-4 h-4" /> People
                  <Badge variant="secondary" className="ml-1 text-xs">{subjects.length + interviews.length}</Badge>
                  <ChevronDown className="w-3 h-3 ml-1" />
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-auto rounded-t-xl">
                <SheetHeader><SheetTitle>People</SheetTitle></SheetHeader>
                <div className="py-4 space-y-2">
                  {[
                    { value: 'subjects', label: 'Subjects', count: subjects.length, icon: User },
                    { value: 'interviews', label: 'Interviews', count: interviews.length, icon: MessageSquare },
                  ].map(item => (
                    <button
                      key={item.value}
                      onClick={() => setActiveTab(item.value)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg ${
                        activeTab === item.value ? 'bg-[#0F172A] text-white' : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      <Badge variant="secondary">{item.count}</Badge>
                    </button>
                  ))}
                </div>
              </SheetContent>
            </Sheet>

            <button 
              onClick={() => setActiveTab('evidence')}
              className={`flex-shrink-0 flex items-center gap-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'evidence' ? 'bg-[#0F172A] text-white' : 'bg-white border border-slate-200 text-slate-600'
              }`}
            >
              <Camera className="w-4 h-4 mr-1" /> Evidence
              <Badge variant="secondary" className="ml-1 text-xs">{caseData.evidence?.length || 0}</Badge>
            </button>

            {/* Warrant Operations - Only for Bail Bond / Warrant cases */}
            {['BAIL_BOND_APPREHENSION', 'BAIL_BOND', 'WARRANT', 'FUGITIVE_RECOVERY', 'SKIP_TRACE'].includes(caseData.case_type) && (
              <button 
                onClick={() => setActiveTab('warrant_ops')}
                className={`flex-shrink-0 flex items-center gap-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'warrant_ops' ? 'bg-[#0F172A] text-white' : 'bg-white border border-slate-200 text-slate-600'
                }`}
                data-testid="warrant-ops-tab-btn"
              >
                <Scale className="w-4 h-4 mr-1" /> ATA Warrants
              </button>
            )}

            <button 
              onClick={() => setActiveTab('reports')}
              className={`flex-shrink-0 flex items-center gap-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'reports' ? 'bg-[#0F172A] text-white' : 'bg-white border border-slate-200 text-slate-600'
              }`}
            >
              <FileText className="w-4 h-4 mr-1" /> Reports
              <Badge variant="secondary" className="ml-1 text-xs">{caseData.reports?.length || 0}</Badge>
            </button>

            {/* More Menu */}
            <Sheet>
              <SheetTrigger asChild>
                <button className="flex-shrink-0 flex items-center gap-1 px-4 py-2.5 rounded-lg text-sm font-medium bg-white border border-slate-200 text-slate-600">
                  <MoreHorizontal className="w-4 h-4" /> More
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-auto max-h-[70vh] rounded-t-xl overflow-y-auto">
                <SheetHeader><SheetTitle>All Sections</SheetTitle></SheetHeader>
                <div className="py-4 space-y-2">
                  {[
                    { value: 'vehicles', label: 'Vehicles', count: vehicles.length, icon: Car },
                    { value: 'interviews', label: 'Interviews', count: interviews.length, icon: MessageSquare },
                    { value: 'locations', label: 'Locations', count: locations.length, icon: MapPin },
                    { value: 'timeline', label: 'Timeline', count: timeline.length, icon: Timer },
                    { value: 'leads', label: 'Leads', count: leads.length, icon: Lightbulb },
                    { value: 'field_ops', label: 'Field Ops', count: 0, icon: MapPin },
                    { value: 'evidence', label: 'Evidence', count: caseData.evidence?.length || 0, icon: Camera },
                    ...(['BAIL_BOND_APPREHENSION','BAIL_BOND','WARRANT','FUGITIVE_RECOVERY','SKIP_TRACE'].includes(caseData.case_type) ? [{ value: 'warrant_ops', label: 'ATA Warrants', count: 0, icon: Scale }] : []),
                    { value: 'logs', label: 'Logs', count: caseData.logs?.length || 0, icon: ClipboardList },
                    { value: 'notes', label: 'Notes', count: caseData.notes?.length || 0, icon: StickyNote },
                    { value: 'messages', label: 'Messages', count: 0, icon: MessagesSquare },
                    ...(isAdmin ? [{ value: 'assignments', label: 'Assignments', count: caseData.assignments?.length || 0, icon: UserPlus }] : []),
                  ].map(item => (
                    <button
                      key={item.value}
                      onClick={() => setActiveTab(item.value)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg ${
                        activeTab === item.value ? 'bg-[#0F172A] text-white' : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      <Badge variant="secondary">{item.count}</Badge>
                    </button>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <CaseOverviewTab 
            caseData={caseData}
            subjects={subjects}
            vehicles={vehicles}
            locations={locations}
            leads={leads}
            interviews={interviews}
            timeline={timeline}
          />
        </TabsContent>

        {/* Iter121I — Time & Billing */}
        <TabsContent value="time">
          <TimeBillingTab caseId={caseId} isAdmin={isAdmin} />
        </TabsContent>

        {/* Iter121I — Field Operations */}
        <TabsContent value="field_ops">
          <FieldOpsTab caseId={caseId} isAdmin={isAdmin} onEndAssignment={() => { setActiveTab('reports'); fetchCase && fetchCase(); }} />
        </TabsContent>

        <TabsContent value="ops_plan">
          <OpsPlanTab caseId={caseId} caseData={caseData} />
        </TabsContent>

        <TabsContent value="photos">
          <PhotoGalleryTab caseId={caseId} />
        </TabsContent>

        {/* Subjects Tab */}
        <TabsContent value="subjects">
          <SubjectsTabEnhanced
            caseId={caseId}
            subjects={subjects}
            canEdit={canEdit}
            isAdmin={isAdmin}
            token={token}
            onRefresh={fetchCase}
            onViewItem={openViewDialog}
            onEditItem={openEditDialog}
            onDeleteItem={deleteEntity}
          />
        </TabsContent>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles">
          <VehiclesTab
            caseId={caseId}
            vehicles={vehicles}
            canEdit={canEdit}
            isAdmin={isAdmin}
            token={token}
            onRefresh={fetchCase}
            onViewItem={openViewDialog}
            onEditItem={openEditDialog}
            onDeleteItem={deleteEntity}
          />
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <TimelineTab
            caseId={caseId}
            timeline={timeline}
            canEdit={canEdit}
            isAdmin={isAdmin}
            token={token}
            onRefresh={fetchIntelligence}
            onViewItem={openViewDialog}
            onEditItem={openEditDialog}
            onDeleteItem={deleteEntity}
          />
        </TabsContent>

        {/* Leads Tab */}
        <TabsContent value="leads">
          <LeadsTab
            caseId={caseId}
            leads={leads}
            canEdit={canEdit}
            isAdmin={isAdmin}
            token={token}
            onRefresh={fetchIntelligence}
            onViewItem={openViewDialog}
            onEditItem={openEditDialog}
            onDeleteItem={deleteEntity}
          />
        </TabsContent>

        {/* Interviews Tab */}
        <TabsContent value="interviews">
          <InterviewsTab
            caseId={caseId}
            interviews={interviews}
            subjects={subjects}
            canEdit={canEdit}
            isAdmin={isAdmin}
            token={token}
            onRefresh={fetchIntelligence}
            onViewItem={openViewDialog}
            onEditItem={openEditDialog}
            onDeleteItem={deleteEntity}
          />
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations">
          <LocationsTab
            caseId={caseId}
            locations={locations}
            subjects={subjects}
            vehicles={vehicles}
            canEdit={canEdit}
            isAdmin={isAdmin}
            token={token}
            onRefresh={fetchIntelligence}
            onViewItem={openViewDialog}
            onEditItem={openEditDialog}
            onDeleteItem={deleteEntity}
          />
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <div className="space-y-4">
            {canEdit && (
              <div className="flex justify-end">
                <Button className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" onClick={() => navigate(`/cases/${caseId}/logs/new`)} data-testid="add-log-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Log Entry
                </Button>
              </div>
            )}
            {(!caseData.logs || caseData.logs.length === 0) ? (
              <Card className="bg-[#131A2A] border-[#334155]">
                <CardContent className="p-8 text-center">
                  <ClipboardList className="w-12 h-12 mx-auto text-[#475569] mb-2" />
                  <p className="text-[#94A3B8]">No case logs yet</p>
                </CardContent>
              </Card>
            ) : (
              caseData.logs.map(log => (
                <Card key={log.id} className="bg-[#131A2A] border-[#334155] hover:border-[#475569] transition-colors" data-testid={`log-entry-${log.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-[#3B82F6]" />
                          <span className="text-[#F8FAFC] font-medium">{log.date}</span>
                          <span className="text-[#94A3B8]">{log.time_start}{log.time_end ? ` - ${log.time_end}` : ''}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#182136]"
                          onClick={() => openViewDialog(log, 'log')}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        {canEdit && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-[#94A3B8] hover:text-[#3B82F6] hover:bg-[#182136]"
                            onClick={() => openEditDialog(log, 'log')}
                            data-testid={`log-edit-${log.id}`}
                          >
                            <Edit className="w-4 h-4 mr-1" /> Edit
                          </Button>
                        )}
                        {isAdmin && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-[#94A3B8] hover:text-[#DC2626] hover:bg-[#182136]"
                            onClick={() => handleDeleteLog(log.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {log.location_address && (
                      <div className="flex items-center gap-1 text-xs text-[#94A3B8] mb-2">
                        <MapPin className="w-3 h-3" /> {[log.location_address, log.city, log.state].filter(Boolean).join(', ')}
                      </div>
                    )}
                    <p className="text-[#F8FAFC] text-sm whitespace-pre-wrap line-clamp-3">{log.narrative}</p>
                    {log.activity_observed && <p className="text-xs text-[#94A3B8] mt-2">Activity: {log.activity_observed}</p>}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#334155] text-xs text-[#94A3B8]">
                      <User className="w-3 h-3" /> {log.investigator_name}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <div className="space-y-4">
            {canEdit && (
              <div className="flex justify-end">
                <Button className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" onClick={() => navigate(`/cases/${caseId}/notes/new`)} data-testid="add-note-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Note
                </Button>
              </div>
            )}
            {(!caseData.notes || caseData.notes.length === 0) ? (
              <Card className="bg-[#131A2A] border-[#334155]">
                <CardContent className="p-8 text-center">
                  <StickyNote className="w-12 h-12 mx-auto text-[#475569] mb-2" />
                  <p className="text-[#94A3B8]">No notes yet</p>
                </CardContent>
              </Card>
            ) : (
              caseData.notes.map(note => (
                <Card key={note.id} className="bg-[#131A2A] border-[#334155] hover:border-[#475569] transition-colors" data-testid={`note-entry-${note.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-[#182136] text-[#94A3B8] border border-[#334155] text-xs">{note.category}</Badge>
                        {note.subject && <span className="text-[#F8FAFC] font-medium text-sm">{note.subject}</span>}
                        <span className="flex items-center gap-1 text-xs text-[#94A3B8]">
                          {visibilityIcon(note.visibility)} {note.visibility?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#182136]"
                          onClick={() => openViewDialog(note, 'note')}
                          data-testid={`note-view-${note.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        {canEdit && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-[#94A3B8] hover:text-[#3B82F6] hover:bg-[#182136]"
                            onClick={() => openEditDialog(note, 'note')}
                            data-testid={`note-edit-${note.id}`}
                          >
                            <Edit className="w-4 h-4 mr-1" /> Edit
                          </Button>
                        )}
                        {isAdmin && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-[#94A3B8] hover:text-[#DC2626] hover:bg-[#182136]"
                            onClick={() => handleDeleteNote(note.id)}
                            data-testid={`note-delete-${note.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-[#F8FAFC] text-sm whitespace-pre-wrap line-clamp-4">{note.content}</p>
                    {note.follow_up_required && (
                      <Badge className="bg-[#D97706]/20 text-[#FBBF24] text-xs mt-2 border border-[#D97706]/30">
                        Follow-up Required {note.follow_up_date ? `by ${note.follow_up_date}` : ''}
                      </Badge>
                    )}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#334155] text-xs text-[#94A3B8]">
                      <User className="w-3 h-3" /> {note.author_name} &middot; {new Date(note.created_at).toLocaleString()}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Evidence Tab */}
        <TabsContent value="evidence">
          <div className="space-y-4">
            <div className="flex justify-end gap-2 flex-wrap">
              {isAdmin && (
                <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => setShowEvidenceTrash(true)} data-testid="evidence-trash-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Trash / Restore
                </Button>
              )}
              <Button className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" onClick={() => navigate(`/cases/${caseId}/evidence/new`)} data-testid="upload-evidence-btn">
                <Upload className="w-4 h-4 mr-2" /> Upload Evidence
              </Button>
            </div>
            {(!caseData.evidence || caseData.evidence.filter(e => !e.is_deleted).length === 0) ? (
              <Card className="bg-[#131A2A] border-[#334155]">
                <CardContent className="p-8 text-center">
                  <Camera className="w-12 h-12 mx-auto text-[#475569] mb-2" />
                  <p className="text-[#94A3B8]">No evidence uploaded</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {caseData.evidence.filter(e => !e.is_deleted).map(ev => (
                  <Card key={ev.id} className="bg-[#131A2A] border-[#334155] hover:border-[#475569] transition-colors" data-testid={`evidence-item-${ev.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <Badge className="bg-[#182136] text-[#94A3B8] border border-[#334155] text-xs mb-2">{ev.evidence_type}</Badge>
                          <p className="text-[#F8FAFC] text-sm font-medium">{ev.file_name}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#182136] h-8 w-8 p-0"
                            onClick={() => navigate(`/cases/${caseId}/evidence/${ev.id}/preview`)}
                            title="Preview file"
                            data-testid={`preview-evidence-${ev.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-[#94A3B8] hover:text-[#0891B2] hover:bg-[#182136] h-8 w-8 p-0"
                            onClick={() => openEvidenceCorrelation(ev)} 
                            data-testid={`correlate-evidence-${ev.id}`}
                            title="Link to entities"
                          >
                            <Network className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#94A3B8] hover:text-[#059669] hover:bg-[#182136] h-8 w-8 p-0"
                            onClick={() => handleEvidenceDownload(ev)}
                            data-testid={`download-evidence-${ev.id}`}
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-[#94A3B8] hover:text-[#DC2626] hover:bg-[#182136] h-8 w-8 p-0"
                              onClick={() => handleDeleteEvidence(ev.id)}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {ev.description && <p className="text-[#94A3B8] text-sm mb-2">{ev.description}</p>}
                      
                      {/* Show correlations */}
                      {(ev.related_subjects?.length > 0 || ev.related_vehicles?.length > 0 || ev.related_locations?.length > 0) && (
                        <div className="mt-3 pt-3 border-t border-[#334155]">
                          <p className="text-xs text-[#94A3B8] mb-2">Linked to:</p>
                          <div className="flex flex-wrap gap-1">
                            {ev.related_subjects?.map(subId => {
                              const sub = subjects.find(s => s.id === subId);
                              return sub ? (
                                <Badge key={subId} className="bg-[#1D4ED8]/20 text-[#3B82F6] text-xs border border-[#1D4ED8]/30">{sub.full_name}</Badge>
                              ) : null;
                            })}
                            {ev.related_vehicles?.map(vehId => {
                              const veh = vehicles.find(v => v.id === vehId);
                              return veh ? (
                                <Badge key={vehId} className="bg-[#7C3AED]/20 text-[#A78BFA] text-xs border border-[#7C3AED]/30">{veh.license_plate}</Badge>
                              ) : null;
                            })}
                            {ev.related_locations?.map(locId => {
                              const loc = locations.find(l => l.id === locId);
                              return loc ? (
                                <Badge key={locId} className="bg-[#059669]/20 text-[#34D399] text-xs border border-[#059669]/30">{loc.name}</Badge>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#334155] text-xs text-[#94A3B8]">
                        <div className="flex items-center gap-2">
                          <User className="w-3 h-3" /> {ev.uploaded_by_name}
                        </div>
                        <div className="flex items-center gap-3">
                          {ev.file_size && <span>{(ev.file_size / 1024).toFixed(1)} KB</span>}
                          <span>{new Date(ev.uploaded_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <div className="space-y-4">
            <div className="flex justify-end">
              <Link to={`/reports/create/${caseId}`}>
                <Button className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="create-report-btn">
                  <Plus className="w-4 h-4 mr-2" /> Create Report
                </Button>
              </Link>
            </div>
            {(!caseData.reports || caseData.reports.length === 0) ? (
              <Card className="bg-[#131A2A] border-[#334155]">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto text-[#475569] mb-2" />
                  <p className="text-[#94A3B8]">No reports yet</p>
                </CardContent>
              </Card>
            ) : (
              caseData.reports.map(report => {
                const canEditReport = (
                  report.status !== 'APPROVED' &&
                  (isAdmin || report.investigator_id === user?.id || report.author_id === user?.id)
                );
                return (
                  <Card key={report.id} className="bg-[#131A2A] border-[#334155]" data-testid={`report-item-${report.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="outline" className="border-slate-500 text-slate-300 text-xs">{report.report_type}</Badge>
                            <Badge className={`text-xs ${report.status === 'APPROVED' ? 'bg-green-600 text-white' : report.status === 'SUBMITTED' ? 'bg-blue-600 text-white' : 'bg-slate-600 text-slate-100'}`}>{report.status}</Badge>
                          </div>
                          <p className="text-slate-100 font-medium truncate">{report.title}</p>
                          <p className="text-xs text-slate-400 mt-1">By {report.investigator_name} &middot; {new Date(report.created_at).toLocaleString()}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Link to={`/reports/create/${caseId}?view=${report.id}`}>
                            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white" data-testid={`report-view-${report.id}`}>
                              <Eye className="w-4 h-4 mr-1" /> View
                            </Button>
                          </Link>
                          {canEditReport && (
                            <Link to={`/reports/create/${caseId}?edit=${report.id}`}>
                              <Button variant="ghost" size="sm" className="text-blue-300 hover:text-blue-100" data-testid={`report-edit-${report.id}`}>
                                <Edit className="w-4 h-4 mr-1" /> Edit
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Assignments Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="assignments">
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-slate-800" onClick={() => { fetchCompanyUsers(); setShowAssignUser(true); }} data-testid="assign-user-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Assign Investigator
                </Button>
              </div>
              {(!caseData.assignments || caseData.assignments.length === 0) ? (
                <Card className="bg-white border-slate-200">
                  <CardContent className="p-8 text-center">
                    <Users className="w-12 h-12 mx-auto text-slate-600 mb-2" />
                    <p className="text-slate-500">No investigators assigned to this case</p>
                    <p className="text-slate-500 text-sm mt-1">Assign investigators to grant them access to this case</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {caseData.assignments.map(asg => (
                    <Card key={asg.id} className="bg-white border-slate-200" data-testid={`assignment-card-${asg.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                              <User className="w-5 h-5 text-slate-900" />
                            </div>
                            <div>
                              <h3 className="text-slate-800 font-medium">{asg.user_name}</h3>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="border-slate-300 text-slate-300 text-xs">
                                  {asg.assignment_role?.replace(/_/g, ' ')}
                                </Badge>
                                <Badge variant="outline" className="border-slate-300 text-slate-500 text-xs">
                                  {asg.user_role?.replace(/_/g, ' ')}
                                </Badge>
                              </div>
                              {asg.notes && <p className="text-slate-500 text-xs mt-2">{asg.notes}</p>}
                              <p className="text-xs text-slate-500 mt-2">
                                Assigned by {asg.assigned_by_name} on {new Date(asg.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveAssignment(asg.id)}
                            className="text-slate-500 hover:text-red-400 p-1"
                            data-testid={`remove-assignment-${asg.id}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Cross-Company Sharing */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900 text-sm">Cross-Company Sharing</h3>
                    <p className="text-xs text-slate-500">Share this case with other companies</p>
                  </div>
                  <Button variant="outline" className="border-slate-300 text-slate-700" 
                    onClick={() => navigate(`/cases/${caseId}/share`)} data-testid="share-case-btn">
                    <FolderOpen className="w-4 h-4 mr-2" /> Share Case
                  </Button>
                </div>
                {caseShares.length > 0 && (
                  <div className="space-y-2">
                    {caseShares.map(share => (
                      <div key={share.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid={`share-card-${share.id}`}>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-cyan-100 text-cyan-700 text-[10px]">SHARED</Badge>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{share.target_company_name}</p>
                            <p className="text-xs text-slate-500">Permission: {share.permission} | By {share.shared_by_name}</p>
                          </div>
                        </div>
                        <button onClick={() => handleRevokeShare(share.id)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`revoke-share-${share.id}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        )}

        {/* Warrant Operations Tab - Only for Bail Bond / Warrant cases */}
        {['BAIL_BOND_APPREHENSION', 'BAIL_BOND', 'WARRANT', 'FUGITIVE_RECOVERY', 'SKIP_TRACE'].includes(caseData.case_type) && (
          <TabsContent value="warrant_ops">
            <WarrantOperationsTab
              caseId={caseId}
              caseData={caseData}
              subjects={subjects}
              token={token}
              isAdmin={isAdmin}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Legacy dialogs (Log, Note, Upload Evidence, Status, Lead, Interview,
          Share Case) have been converted to dedicated routed pages under
          `/cases/:caseId/{logs|notes|evidence|status|leads|interviews|share}/…`.
          Trigger buttons now `navigate(…)` instead of opening popups. */}

      {/* Add Timeline Event Dialog */}
      <Dialog open={showAddTimeline} onOpenChange={setShowAddTimeline}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg">
          <DialogHeader><DialogTitle>Add Timeline Event</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Event Type</Label>
                <Select value={timelineForm.event_type} onValueChange={v => setTimelineForm({ ...timelineForm, event_type: v })}>
                  <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CUSTOM">Custom Event</SelectItem>
                    <SelectItem value="SUBJECT_SIGHTING">Subject Sighting</SelectItem>
                    <SelectItem value="INTERVIEW_CONDUCTED">Interview Conducted</SelectItem>
                    <SelectItem value="LEAD_RECEIVED">Lead Received</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Event Date</Label><Input type="date" value={timelineForm.event_date} onChange={e => setTimelineForm({ ...timelineForm, event_date: e.target.value })} className="bg-slate-50 border-slate-300 text-slate-900" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Event Time</Label><Input type="time" value={timelineForm.event_time} onChange={e => setTimelineForm({ ...timelineForm, event_time: e.target.value })} className="bg-slate-50 border-slate-300 text-slate-900" /></div>
              <div><Label>Location</Label><Input value={timelineForm.location} onChange={e => setTimelineForm({ ...timelineForm, location: e.target.value })} className="bg-slate-50 border-slate-300 text-slate-900" /></div>
            </div>
            <div><Label>Description *</Label><Textarea value={timelineForm.description} onChange={e => setTimelineForm({ ...timelineForm, description: e.target.value })} className="bg-slate-50 border-slate-300 text-slate-900" rows={3} data-testid="timeline-description-input" /></div>
            <Button onClick={handleAddTimelineEvent} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-slate-800" data-testid="submit-timeline-btn">Add Event</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Location Dialog is rendered inside LocationsTab */}

      {/* Evidence Correlation Dialog */}
      <Dialog open={showEvidenceCorrelation} onOpenChange={(open) => { setShowEvidenceCorrelation(open); if (!open) setSelectedEvidence(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Link Evidence: {selectedEvidence?.file_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Subjects */}
            <div>
              <Label className="text-sm text-slate-500 mb-2 block">Link to Subjects</Label>
              <div className="flex flex-wrap gap-2">
                {subjects.length === 0 ? (
                  <p className="text-xs text-slate-500">No subjects to link</p>
                ) : subjects.map(sub => (
                  <button
                    key={sub.id}
                    onClick={() => toggleCorrelation('related_subjects', sub.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      evidenceCorrelation.related_subjects?.includes(sub.id)
                        ? 'bg-blue-500 text-slate-800'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {sub.full_name}
                  </button>
                ))}
              </div>
            </div>

            {/* Vehicles */}
            <div>
              <Label className="text-sm text-slate-500 mb-2 block">Link to Vehicles</Label>
              <div className="flex flex-wrap gap-2">
                {vehicles.length === 0 ? (
                  <p className="text-xs text-slate-500">No vehicles to link</p>
                ) : vehicles.map(veh => (
                  <button
                    key={veh.id}
                    onClick={() => toggleCorrelation('related_vehicles', veh.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      evidenceCorrelation.related_vehicles?.includes(veh.id)
                        ? 'bg-purple-500 text-slate-800'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {veh.license_plate} {veh.make && `- ${veh.make}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Locations */}
            <div>
              <Label className="text-sm text-slate-500 mb-2 block">Link to Locations</Label>
              <div className="flex flex-wrap gap-2">
                {locations.length === 0 ? (
                  <p className="text-xs text-slate-500">No locations to link</p>
                ) : locations.map(loc => (
                  <button
                    key={loc.id}
                    onClick={() => toggleCorrelation('related_locations', loc.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      evidenceCorrelation.related_locations?.includes(loc.id)
                        ? 'bg-green-500 text-slate-800'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {loc.name}
                  </button>
                ))}
              </div>
            </div>

            <div><Label>Correlation Notes</Label><Textarea value={evidenceCorrelation.correlation_notes} onChange={e => setEvidenceCorrelation({ ...evidenceCorrelation, correlation_notes: e.target.value })} className="bg-slate-50 border-slate-300 text-slate-900" rows={2} placeholder="Notes about how this evidence relates to linked entities..." /></div>
            
            <Button onClick={handleSaveEvidenceCorrelation} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-slate-800" data-testid="save-correlation-btn">Save Correlations</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Evidence Trash / Restore Dialog — Iter128b */}
      <Dialog open={showEvidenceTrash} onOpenChange={setShowEvidenceTrash}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="evidence-trash-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <Trash2 className="w-5 h-5 text-red-500" /> Evidence Trash · Restore Files
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Soft-deleted evidence. The file still lives in Object Storage — restore it any time.
            </p>
          </DialogHeader>
          <div className="mt-4 space-y-2">
            {loadingTrash ? (
              <div className="text-sm text-slate-400 py-6 text-center">Loading trash…</div>
            ) : deletedEvidence.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">Trash is empty. Nothing to restore.</div>
            ) : (
              deletedEvidence.map(ev => (
                <div key={ev.id} className="flex items-center justify-between p-3 border border-slate-200 rounded" data-testid={`trash-item-${ev.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">{ev.file_name}</div>
                    <div className="text-xs text-slate-500">
                      Deleted {ev.deleted_at ? new Date(ev.deleted_at).toLocaleString() : ''} · {ev.evidence_type || 'evidence'}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => restoreEvidence(ev.id)}
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                    data-testid={`restore-btn-${ev.id}`}
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Restore
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* County Assignment Dialog — Iter126 */}
      <Dialog open={showCountyDialog} onOpenChange={setShowCountyDialog}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl" data-testid="county-assign-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-sky-600" /> Assign State · City · County
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Set the geographic scope of this case so it plots on the Dashboard Case Map.
            </p>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {countyForm && (
              <CountyAssignmentPicker
                value={countyForm}
                onChange={(v) => setCountyForm(v)}
                disabled={savingCounty}
              />
            )}
            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setShowCountyDialog(false)} className="flex-1" data-testid="county-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!countyForm?.state || !countyForm?.county) {
                    toast({ title: 'Missing selection', description: 'Please pick State, City, and County.', variant: 'destructive' });
                    return;
                  }
                  setSavingCounty(true);
                  try {
                    const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({
                        state: countyForm.state,
                        state_name: countyForm.state_name,
                        city: countyForm.city,
                        county: countyForm.county,
                        latitude: countyForm.latitude,
                        longitude: countyForm.longitude,
                      }),
                    });
                    if (res.ok) {
                      toast({ title: 'County assigned', description: `${countyForm.city}, ${countyForm.county} County, ${countyForm.state}` });
                      setShowCountyDialog(false);
                      fetchCase();
                    } else {
                      const err = await res.json().catch(() => ({}));
                      toast({ title: 'Failed to save', description: err.detail || 'Server error', variant: 'destructive' });
                    }
                  } catch (e) {
                    toast({ title: 'Error', description: e.message, variant: 'destructive' });
                  } finally {
                    setSavingCounty(false);
                  }
                }}
                className="flex-1 bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
                disabled={savingCounty}
                data-testid="county-save-btn"
              >
                {savingCounty ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MapPin className="w-4 h-4 mr-2" />}
                Save Assignment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign User Dialog — full-screen page-style */}
      <Dialog open={showAssignUser} onOpenChange={setShowAssignUser}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-full w-screen h-screen sm:h-screen sm:rounded-none p-0 overflow-y-auto" data-testid="assign-user-dialog">
          <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
            <DialogHeader className="p-0 space-y-0">
              <DialogTitle className="text-xl font-bold">Assign Investigator to Case</DialogTitle>
              <p className="text-xs text-slate-500 mt-1">Assign a user to this case with a role and optional notes.</p>
            </DialogHeader>
            <Button variant="ghost" onClick={() => setShowAssignUser(false)} className="text-slate-600" data-testid="assign-user-close">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="max-w-3xl mx-auto p-6 space-y-6">
            <div>
              <Label className="text-sm font-semibold">Select Investigator *</Label>
              <Select value={assignForm.user_id} onValueChange={v => setAssignForm({ ...assignForm, user_id: v })}>
                <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900 h-11" data-testid="assign-user-select">
                  <SelectValue placeholder="Choose an investigator..." />
                </SelectTrigger>
                <SelectContent>
                  {companyUsers.filter(u => 
                    !caseData?.assignments?.some(a => a.user_id === u.id)
                  ).map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.role?.replace(/_/g, ' ')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold">Assignment Role</Label>
              <Select value={assignForm.assignment_role} onValueChange={v => setAssignForm({ ...assignForm, assignment_role: v })}>
                <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900 h-11" data-testid="assignment-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="chief">Chief</SelectItem>
                  <SelectItem value="assistant_chief">Assistant Chief</SelectItem>
                  <SelectItem value="captain">Captain</SelectItem>
                  <SelectItem value="lieutenant">Lieutenant</SelectItem>
                  <SelectItem value="intelligence_commander">Intelligence Commander</SelectItem>
                  <SelectItem value="field_sergeant">Field Sergeant</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="lead_investigator">Lead Investigator</SelectItem>
                  <SelectItem value="field_investigator">Field Investigator</SelectItem>
                  <SelectItem value="support_investigator">Support Investigator</SelectItem>
                  <SelectItem value="skip_tracer">Skip Tracer</SelectItem>
                  <SelectItem value="transporter">Transporter</SelectItem>
                  <SelectItem value="analyst">Analyst</SelectItem>
                  <SelectItem value="case_manager">Case Manager</SelectItem>
                  <SelectItem value="trainee">Trainee</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold">Notes</Label>
              <Textarea 
                value={assignForm.notes} 
                onChange={e => setAssignForm({ ...assignForm, notes: e.target.value })} 
                className="bg-slate-50 border-slate-300 text-slate-900 min-h-[100px]" 
                rows={4} 
                placeholder="Assignment notes (optional)..." 
              />
            </div>
            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setShowAssignUser(false)} className="flex-1" data-testid="assign-user-cancel">Cancel</Button>
              <Button onClick={handleAssignUser} className="flex-1 bg-[#0F172A] hover:bg-[#1E293B] text-white h-11" data-testid="confirm-assign-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Assign to Case
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Client Access Dialog */}
      <Dialog open={showAssignClient} onOpenChange={setShowAssignClient}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="assign-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Add Client Access
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <Label className="text-slate-700 text-xs">Search Clients</Label>
              <Input
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Search by name, business, email…"
                className="border-slate-300"
                data-testid="assign-client-search-input"
              />
            </div>
            <div className="border border-slate-200 rounded-md max-h-72 overflow-y-auto divide-y divide-slate-100">
              {clientsLoading ? (
                <div className="p-4 text-center text-sm text-slate-500">
                  <Loader2 className="inline w-4 h-4 mr-2 animate-spin" /> Loading clients…
                </div>
              ) : (() => {
                const q = clientSearch.trim().toLowerCase();
                const filtered = q
                  ? allClients.filter(c => {
                      const bag = [
                        c.first_name, c.last_name, c.business_name, c.name,
                        c.primary_email, c.email, c.phone, c.portal_username,
                      ].filter(Boolean).join(' ').toLowerCase();
                      return bag.includes(q);
                    })
                  : allClients;
                // Iter121D — hide clients that already have access to this case.
                const attachedIds = new Set(attachedClients.map((c) => c.id));
                const notYetAssigned = filtered.filter((c) => !attachedIds.has(c.id));
                if (notYetAssigned.length === 0) {
                  return (
                    <div className="p-4 text-center text-sm text-slate-500">
                      {q ? 'No clients match.' : 'All eligible clients already have access.'}
                    </div>
                  );
                }
                return notYetAssigned.slice(0, 100).map(c => {
                  const display = c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.business_name || 'Unnamed';
                  const email = c.primary_email || c.email || '';
                  const selected = pendingClientId === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setPendingClientId(c.id)}
                      className={`w-full text-left p-3 hover:bg-slate-50 transition-colors ${selected ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : ''}`}
                      data-testid={`assign-client-option-${c.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-800 truncate">{display}</div>
                          {c.business_name && c.business_name !== display && (
                            <div className="text-xs text-slate-500 truncate">{c.business_name}</div>
                          )}
                          {email && <div className="text-xs text-slate-500 truncate">{email}</div>}
                          {c.phone && <div className="text-xs text-slate-400">{c.phone}</div>}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${c.portal_enabled ? 'border-emerald-500 text-emerald-700' : 'border-slate-400 text-slate-500'}`}
                          >
                            {c.portal_enabled ? 'Portal On' : 'Portal Off'}
                          </Badge>
                        </div>
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowAssignClient(false)}
                className="border-slate-300 text-slate-700"
                data-testid="assign-client-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignClient}
                disabled={!pendingClientId || assigningClient}
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
                data-testid="assign-client-save"
              >
                {assigningClient ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Add Client'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Case Dialog */}
      {/* Removed — converted to routed page /cases/:caseId/share (ShareCasePage) */}

      {/* View/Edit Detail Dialog */}
      <Dialog open={!!viewItem} onOpenChange={(open) => { if (!open) { setViewItem(null); setEditMode(false); } }}>
        <DialogContent className="bg-[#131A2A] border-[#334155] text-[#F8FAFC] max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="capitalize text-[#F8FAFC]">{viewType} Details</DialogTitle>
              <div className="flex items-center gap-2">
                {!editMode && canEdit && (
                  <Button size="sm" variant="outline" className="border-[#334155] text-[#94A3B8] hover:bg-[#182136] hover:text-[#F8FAFC]"
                    onClick={() => setEditMode(true)} data-testid="edit-record-btn">
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                )}
                {editMode && (
                  <>
                    <Button size="sm" variant="outline" className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]"
                      onClick={() => setEditMode(false)} data-testid="cancel-edit-btn">
                      Cancel
                    </Button>
                    <Button size="sm" className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white"
                      onClick={handleUpdateItem} data-testid="save-record-btn">
                      Save Changes
                    </Button>
                  </>
                )}
              </div>
            </div>
            {editMode && <Badge className="bg-[#D97706]/20 text-[#FBBF24] border border-[#D97706]/30 text-xs w-fit">Editing Mode</Badge>}
            {!editMode && <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] border border-[#3B82F6]/30 text-xs w-fit">View Only</Badge>}
          </DialogHeader>
          <div className="mt-4">
            {renderViewEditFields()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Evidence File Preview Dialog - inline image/video/pdf viewer */}
      <Dialog open={!!previewEvidence} onOpenChange={(open) => { if (!open) closeEvidencePreview(); }}>
        <DialogContent className="bg-[#0B0F19] border-[#334155] text-[#F8FAFC] max-w-4xl w-[95vw] max-h-[92vh] flex flex-col" data-testid="evidence-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] text-base truncate">
              {previewEvidence?.file_name || 'Evidence Preview'}
            </DialogTitle>
            <div className="flex flex-wrap gap-2 items-center text-xs text-[#94A3B8] mt-1">
              {previewEvidence?.evidence_type && (
                <Badge className="bg-[#182136] text-[#94A3B8] border border-[#334155]">{previewEvidence.evidence_type}</Badge>
              )}
              {previewEvidence?.content_type && <span className="font-mono">{previewEvidence.content_type}</span>}
              {previewEvidence?.file_size && <span className="font-mono">{(previewEvidence.file_size/1024).toFixed(1)} KB</span>}
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-[300px] bg-[#050913] rounded-sm border border-[#1E293B] flex items-center justify-center overflow-auto" data-testid="evidence-preview-body">
            {previewLoading && (
              <div className="text-[#94A3B8] text-sm">Loading preview…</div>
            )}
            {!previewLoading && previewError && (
              <div className="text-center p-6">
                <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                <p className="text-amber-300 font-semibold mb-1">Preview unavailable</p>
                <p className="text-[#94A3B8] text-xs max-w-md">{previewError}</p>
                <div className="flex gap-2 justify-center mt-4 flex-wrap">
                  <Button
                    onClick={async () => {
                      if (!previewEvidence?.id) return;
                      try {
                        const r = await fetch(`${BACKEND_URL}/api/evidence/${previewEvidence.id}/attempt-recovery`, {
                          method: 'POST', headers: { Authorization: `Bearer ${token}` }
                        });
                        const d = await r.json().catch(() => ({}));
                        if (d.recovered) {
                          toast({ title: 'File recovered', description: (d.reason || '').replace(/_/g, ' ') });
                          openEvidencePreview(previewEvidence);
                          fetchCase();
                        } else {
                          toast({ title: 'Auto-recover failed', description: (d.reason || 'file missing').replace(/_/g, ' '), variant: 'destructive' });
                        }
                      } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
                    }}
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold text-xs"
                    data-testid="ev-preview-recover-btn"
                  >
                    Restore File
                  </Button>
                  <Button
                    variant="outline"
                    className="border-[#334155] text-slate-300 text-xs"
                    onClick={() => previewEvidence && openEvidencePreview(previewEvidence)}
                    data-testid="ev-preview-retry-btn"
                  >
                    Retry
                  </Button>
                </div>
              </div>
            )}
            {!previewLoading && !previewError && previewBlobUrl && previewEvidence && (() => {
              const ct = (previewEvidence.content_type || '').toLowerCase();
              const name = (previewEvidence.file_name || '').toLowerCase();
              const isImg = ct.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/.test(name);
              const isVid = ct.startsWith('video/') || /\.(mp4|mov|webm|avi|wmv)$/.test(name);
              const isAud = ct.startsWith('audio/') || /\.(mp3|wav|ogg|aac|flac)$/.test(name);
              const isPdf = ct === 'application/pdf' || name.endsWith('.pdf');
              if (isImg) return <img src={previewBlobUrl} alt={previewEvidence.file_name} style={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain' }} data-testid="evidence-preview-image" />;
              if (isVid) return <video src={previewBlobUrl} controls style={{ maxWidth: '100%', maxHeight: '78vh' }} data-testid="evidence-preview-video" />;
              if (isAud) return <audio src={previewBlobUrl} controls className="w-full" data-testid="evidence-preview-audio" />;
              if (isPdf) return <iframe src={previewBlobUrl} title="pdf-preview" style={{ width: '100%', height: '78vh', border: 0 }} data-testid="evidence-preview-pdf" />;
              return (
                <div className="text-center p-6">
                  <FileText className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
                  <p className="text-[#F8FAFC] text-sm mb-1">Preview not supported for this file type.</p>
                  <p className="text-[#94A3B8] text-xs mb-4">Download the file to open it in its native app.</p>
                </div>
              );
            })()}
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-[#1E293B] mt-2">
            <p className="text-xs text-[#94A3B8] truncate pr-4">
              {previewEvidence?.description || 'No description'}
            </p>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                variant="outline"
                className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]"
                onClick={() => previewEvidence && handleEvidenceDownload(previewEvidence)}
                data-testid="evidence-preview-download-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
              <Button onClick={closeEvidencePreview} className="bg-[#1D4ED8] hover:bg-[#1E40AF]" data-testid="evidence-preview-close-btn">Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter123 — Case Closure Wizard */}
      <CaseClosureWizard
        open={showClosureWizard}
        caseData={caseData}
        onClose={() => setShowClosureWizard(false)}
        onClosed={() => { setShowClosureWizard(false); window.location.reload(); }}
      />
    </div>
  );
};

export default CaseDetail;
